import React, { useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";

import { TextField } from "components/Form";

import { useUpdatePaymentMethod } from "api/payments/index.js";

/**
 * RenamePaymentMethod - Component to conditionally display a modal that allows the ability to rename a payment method.
 * @param {RenamePaymentMethodProps} props
 * @param {Boolean} props.isOpen - Dictates whether modal is open or closed.
 * @param {Function} props.onClose - function to close modal.
 * @param {Object} props.paymentMethod - The payment method that this component is responsible for.
 * @param {Function} props.onComplete - callback function to handle success case.
 * @returns JSX.Element
 */
const RenamePaymentMethodModal = ({ isOpen, onClose, paymentMethod, onComplete }) => {
  const { formatMessage } = useIntl();
  const {
    mutateAsync: updatePaymentMethod,
    isSuccess: isUpdatePMSuccess,
    isLoading: isUpdatingPM,
    error: updatePMError,
  } = useUpdatePaymentMethod();

  const toast = useToast();

  const handleSubmit = async ({ displayString }) => {
    updatePaymentMethod({ id: paymentMethod.id, displayString });
  };

  useEffect(() => {
    if (isUpdatePMSuccess) {
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Payment method successfully renamed",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      onComplete();
      onClose();
    }
  }, [isUpdatePMSuccess, onComplete, toast, onClose, formatMessage]);

  useEffect(() => {
    if (updatePMError) {
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description: updatePMError.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [updatePMError, toast, formatMessage]);
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <Formik
        initialValues={{ displayString: paymentMethod.display_string }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          displayString: Yup.string()
            .max(
              36,
              formatMessage({
                defaultMessage: "New name must be 36 characters or less",
                description: "Input error message",
              })
            )
            .required(
              formatMessage({
                defaultMessage: "New name cannot be empty",
                description: "Input error message",
              })
            ),
        })}
      >
        {({ isValid, values }) => (
          <Form>
            <ModalContent>
              <ModalHeader>
                <FormattedMessage
                  defaultMessage="Rename Payment Method"
                  description="Modal header"
                />
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <TextField
                  name="displayString"
                  label={formatMessage({
                    defaultMessage: "New name",
                    description: "Input label",
                  })}
                  maxLength="36"
                />
              </ModalBody>
              <ModalFooter justifyContent="flex-end">
                <Button
                  type="submit"
                  isDisabled={isUpdatingPM || !isValid || !values.displayString.trim()}
                >
                  <FormattedMessage defaultMessage="Submit" description="button label" />
                </Button>
              </ModalFooter>
            </ModalContent>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default RenamePaymentMethodModal;
